import { useEffect, useMemo, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import useReveal from "@/hooks/useReveal";
import { useSiteAssets } from "@/context/SiteAssetsContext";

const CATEGORY_ORDER = ["renovation", "bathroom", "solar_ac", "painting", "ceramica"];

export default function Gallery() {
  const { t } = useLang();
  const { gallery } = useSiteAssets();
  const [active, setActive] = useState("all");
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const { ref, visible } = useReveal();

  const categoriesWithItems = useMemo(() => {
    const s = new Set((gallery || []).map((g) => g.category || "bathroom"));
    return CATEGORY_ORDER.filter((c) => s.has(c));
  }, [gallery]);

  useEffect(() => {
    if (active !== "all" && !categoriesWithItems.includes(active)) {
      setActive(categoriesWithItems[0] || "all");
    }
  }, [categoriesWithItems, active]);

  const filtered = useMemo(() => {
    const list = (gallery || []).map((g) => ({ ...g, category: g.category || "bathroom" }));
    if (active === "all") {
      return list.slice().sort((a, b) => {
        const ca = CATEGORY_ORDER.indexOf(a.category);
        const cb = CATEGORY_ORDER.indexOf(b.category);
        if (ca !== cb) return ca - cb;
        return (a.position || 0) - (b.position || 0);
      });
    }
    return list
      .filter((g) => g.category === active)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  }, [gallery, active]);

  const photos = filtered.map((g) => g.data_url);
  const captions = filtered.map((g) => g.caption || "");

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const openAt = (i) => {
    setIdx(i);
    setOpen(true);
  };

  // Lightbox keyboard nav
  const next = (e) => {
    e?.stopPropagation();
    if (!photos.length) return;
    setIdx((i) => (i + 1) % photos.length);
  };
  const prev = (e) => {
    e?.stopPropagation();
    if (!photos.length) return;
    setIdx((i) => (i - 1 + photos.length) % photos.length);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-20 md:py-28 lg:py-32 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`reveal ${visible ? "visible" : ""} max-w-3xl mb-10 md:mb-14`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-[#1F9D40]" />
            <span className="text-xs uppercase tracking-[0.22em] text-neutral-600 font-semibold">
              {t("gallery.eyebrow")}
            </span>
          </div>
          <h2 className="font-display font-black text-neutral-900 text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {t("gallery.title")}
          </h2>
          <p className="mt-5 text-neutral-600 text-base md:text-lg">{t("gallery.subtitle")}</p>
        </div>

        {categoriesWithItems.length > 0 && (
          <div data-testid="gallery-tabs" className="flex gap-2 flex-wrap mb-8 md:mb-10">
            <GalleryTab
              id="all"
              label={t("gallery.tabs.all")}
              active={active === "all"}
              onClick={() => setActive("all")}
            />
            {categoriesWithItems.map((c) => (
              <GalleryTab
                key={c}
                id={c}
                label={t(`gallery.tabs.${c}`)}
                active={active === c}
                onClick={() => setActive(c)}
              />
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="p-10 text-center border border-dashed border-neutral-300 bg-white text-neutral-500 text-sm">
            {t("gallery.empty")}
          </div>
        ) : (
          <GalleryCarousel
            key={active}
            items={filtered}
            onItemClick={openAt}
            tabLabelFor={(cat) => t(`gallery.tabs.${cat}`)}
          />
        )}

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-sm text-neutral-600 max-w-md">{t("gallery.subtitle")}</p>
          <button
            data-testid="gallery-cta"
            onClick={() => scrollTo("contact")}
            className="inline-flex items-center gap-2 bg-[#E11D2E] hover:bg-[#b81624] text-white font-bold px-7 py-4 rounded-full text-sm transition-all shadow-lg shadow-red-900/10 hover:-translate-y-0.5"
          >
            {t("gallery.cta")} →
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {open && photos.length > 0 && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            data-testid="gallery-close"
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="close"
          >
            <X size={20} />
          </button>
          <button
            onClick={prev}
            data-testid="gallery-prev"
            className="absolute left-4 md:left-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="prev"
          >
            <ChevronLeft size={22} />
          </button>
          <img
            src={photos[idx]}
            alt="preview"
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[86vh] object-contain"
          />
          <button
            onClick={next}
            data-testid="gallery-next"
            className="absolute right-4 md:right-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="next"
          >
            <ChevronRight size={22} />
          </button>
          <div className="absolute bottom-6 left-0 right-0 text-center text-white/70 text-xs tracking-widest">
            {captions[idx] ? `${captions[idx]} · ` : ""}
            {idx + 1} / {photos.length}
          </div>
        </div>
      )}
    </section>
  );
}

function GalleryCarousel({ items, onItemClick, tabLabelFor }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: items.length > 3,
    align: "start",
    skipSnaps: false,
  });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onInit = useCallback((api) => {
    if (!api) return;
    setSnaps(api.scrollSnapList());
  }, []);

  const onSelect = useCallback((api) => {
    if (!api) return;
    setSelected(api.selectedScrollSnap());
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();
  const scrollTo = (i) => emblaApi && emblaApi.scrollTo(i);

  return (
    <div data-testid="gallery-carousel" className="relative">
      <div className="overflow-hidden -mx-2" ref={emblaRef}>
        <div className="flex">
          {items.map((g, i) => (
            <div
              key={`${g.id || g.data_url}-${i}`}
              className="px-2 shrink-0 grow-0 basis-full sm:basis-1/2 lg:basis-1/3"
            >
              <button
                data-testid={`gallery-photo-${i}`}
                onClick={() => onItemClick(i)}
                className="group relative w-full aspect-[4/3] overflow-hidden bg-neutral-200"
              >
                <img
                  src={g.data_url}
                  alt={g.caption || tabLabelFor(g.category)}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest text-white bg-[#1F9D40] px-2.5 py-1 rounded-full">
                  {tabLabelFor(g.category)}
                </div>
                <div className="absolute bottom-3 left-3 right-3 text-white text-xs font-semibold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-between">
                  <span className="truncate pr-2">{g.caption || "Alicante"}</span>
                  <span className="w-7 h-7 rounded-full bg-[#1F9D40] flex items-center justify-center shrink-0">
                    +
                  </span>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      {items.length > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center gap-2" data-testid="gallery-dots">
            {snaps.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === selected ? "w-8 bg-neutral-900" : "w-3 bg-neutral-300 hover:bg-neutral-400"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={scrollPrev}
              disabled={!canPrev}
              data-testid="gallery-carousel-prev"
              className="w-11 h-11 rounded-full bg-white border border-neutral-200 flex items-center justify-center hover:border-neutral-900 hover:text-neutral-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={scrollNext}
              disabled={!canNext}
              data-testid="gallery-carousel-next"
              className="w-11 h-11 rounded-full bg-neutral-900 text-white flex items-center justify-center hover:bg-[#1F9D40] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GalleryTab({ id, label, active, onClick }) {
  return (
    <button
      data-testid={`gallery-tab-${id}`}
      onClick={onClick}
      className={`px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-bold uppercase tracking-wider transition-all border ${
        active
          ? "bg-neutral-900 text-white border-neutral-900"
          : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
      }`}
    >
      {label}
    </button>
  );
}
